<template>
  <WdsModal :show="show" @cancel="show = false">
    <template #header>
      <WdsStack gap-md align-center class="tw-mt-5 tw-mb-4">
        <WdsHeading class="tw-my-0" type="title2">
          {{ $t('Download table') }}
        </WdsHeading>
        <div>
          <WdsLabel xs warning subtle>
            Beta
            <WdsIcon
              :size="13"
              weight="thin"
              :name="WdsIconNameEnum.LIGHT"
              class="!tw-inline-block"
            />
          </WdsLabel>
        </div>
      </WdsStack>
    </template>
    <WdsStack gap-md direction-column>
      <WdsInput
        v-model="fileName"
        placeholder="ContractExport"
        :label="$t('File name')"
        :info="$t('No need to add file ending to name')"
      />

      <WdsSegmentedControl
        :default-active-key="fileType"
        :label="$t('Export file as')"
        :options="fileTypeOptions"
        @click="fileType = $event"
      />
      <WdsText>
        {{
          $t(
            'Exports can take a while and may fail if repeated too quickly. Reach out if you experience any issues.',
          )
        }}
      </WdsText>
      <WdsText v-if="err" error>{{ $t('Some error has occurred during the export') }}</WdsText>
    </WdsStack>
    <template #footer>
      <WdsStack justify-space-between>
        <WdsButton @click="show = false">
          {{ $t('Cancel') }}
        </WdsButton>
        <WdsStack gap-sm justify-center align-center>
          <div><WdsSpinner v-if="loading" indeterminate /></div>
          <WdsButton primary :disabled="loading || fileName === ''" @click="exportData">
            {{ $t('Download') }}
          </WdsButton>
        </WdsStack>
      </WdsStack>
    </template>
  </WdsModal>
</template>

<script lang="ts" setup>
import { Ref, ref, watch } from 'vue'
import {
  WdsButton,
  WdsHeading,
  WdsIcon,
  WdsIconNameEnum,
  WdsInput,
  WdsLabel,
  WdsModal,
  WdsSegmentedControl,
  WdsSpinner,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { Kind, TypedQueryDocumentNode, print } from 'graphql'
import { $t } from '@/composables/translation'
import { dataExport, ExportFormat, TableDescriber } from '@/utils/apiDataExports'
import { useQueryType } from '@/components/GraphqlDataTable/composables'

const clientId = 'publicApiCustomFields'

const OperationDefinition = 'OperationDefinition'

const props = defineProps<{
  query: TypedQueryDocumentNode
  variables: Record<string, any>
  dataDescription: TableDescriber<any>
}>()

const show = defineModel<boolean>('show')

const loading = ref(false)
const err: Ref<any> = ref(null)
const { type } = useQueryType(print(props.query))
const fileName = ref(`table-export-${new Date().toDateString()}`)
watch(type, () => {
  if (type.value !== null) {
    fileName.value = `${type.value.name}-export-${new Date().toDateString()}`
  }
})
const fileType = ref(ExportFormat.CSV)
const fileTypeOptions = [
  { optionKey: ExportFormat.CSV, title: 'CSV Sheets' },
  { optionKey: ExportFormat.EXCEL, title: 'Excel Sheet' },
  { optionKey: ExportFormat.JSON, title: 'JSON' },
]

// Function to export the query data, possibly to a CSV or Excel file
const exportData = async () => {
  const dataDescription = { ...props.dataDescription }
  dataDescription.title = fileName.value

  // // Define query options for the export
  const queryOptions = {
    query: props.query,
    variables: props.variables,
  }

  const def = props.query.definitions.find(({ kind }) => kind === OperationDefinition)
  const selectionPath = def?.selectionSet.selections?.[0] // We assume only one query path has been made
  if (selectionPath?.kind === Kind.INLINE_FRAGMENT || !selectionPath?.name?.value) return
  const path = `${selectionPath?.name?.value}.data`

  // // Define data mapping for pagination and list extraction
  const dataMap = {
    perPageVarName: 'first',
    pageVarName: 'page',
    listPath: path,
  }

  try {
    loading.value = true
    err.value = false
    // // Call the 'dataExport' function to perform the export
    await dataExport(dataDescription, queryOptions, dataMap, clientId, fileType.value)
  } catch (error) {
    err.value = true
  } finally {
    loading.value = false
  }
}
</script>

<style lang="scss"></style>
